import { faker } from "@faker-js/faker/locale/en";
import * as z from "zod";
import { generateStringForField } from "./mock-field-generators";

const mockValueCache = new Map<string, unknown>();
const lazySeenSchemas = new WeakSet<object>();

export function generateMockFromSchema(
  schema: z.ZodTypeAny,
  cacheKey?: string,
): unknown {
  if (cacheKey) {
    if (mockValueCache.has(cacheKey)) {
      return mockValueCache.get(cacheKey);
    }
    const value = generate(schema);
    mockValueCache.set(cacheKey, value);
    return value;
  }
  return generate(schema);
}

function generate(schema: z.ZodTypeAny, fieldName?: string): unknown {
  if (!schema) {
    return null;
  }
  if (schema instanceof z.ZodOptional) {
    return generate(schema.unwrap() as z.ZodTypeAny, fieldName);
  }
  if (schema instanceof z.ZodNullable) {
    return generate(schema.unwrap() as z.ZodTypeAny, fieldName);
  }
  if (schema instanceof z.ZodDefault) {
    return generate(schema.def.innerType as z.ZodTypeAny, fieldName);
  }
  if (schema instanceof z.ZodObject) {
    const lower = fieldName?.toLowerCase() ?? "";
    const isImageObject =
      lower === "image" || lower.endsWith("image") || lower.endsWith("images");
    const result: Record<string, unknown> = {};
    for (const [key, field] of Object.entries(
      schema.shape as Record<string, z.ZodTypeAny>,
    )) {
      if (isImageObject && key === "url") {
        result[key] =
          `https://picsum.photos/seed/${faker.string.alphanumeric(8)}/400/400`;
      } else {
        result[key] = generate(field, key);
      }
    }
    return result;
  }
  if (schema instanceof z.ZodArray) {
    const count = faker.number.int({ min: 1, max: 10 });
    return Array.from({ length: count }, () =>
      generate(schema.element as z.ZodTypeAny, fieldName),
    );
  }
  if (schema instanceof z.ZodString) {
    return generateStringForField(fieldName);
  }
  if (schema instanceof z.ZodNumber) {
    return faker.number.int({ min: 1, max: 100 });
  }
  if (schema instanceof z.ZodBoolean) {
    return true;
  }
  if (schema instanceof z.ZodDate) {
    return new Date();
  }
  if (schema instanceof z.ZodNull) {
    return null;
  }
  if (schema instanceof z.ZodUndefined) {
    return undefined;
  }
  if (schema instanceof z.ZodLiteral) {
    return schema.def.values[0];
  }
  if (schema instanceof z.ZodEnum) {
    return schema.options[0];
  }
  if (schema instanceof z.ZodUnion) {
    const options = schema.def.options as z.ZodTypeAny[];
    return options.length > 0 ? generate(options[0]!, fieldName) : null;
  }
  if (schema instanceof z.ZodRecord) {
    const count = faker.number.int({ min: 1, max: 10 });
    const result: Record<string, unknown> = {};
    for (let i = 0; i < count; i++) {
      const key = generate(schema.def.keyType as z.ZodTypeAny) as string;
      result[key] = generate(schema.def.valueType as z.ZodTypeAny);
    }
    return result;
  }
  if (schema instanceof z.ZodMap) {
    const count = faker.number.int({ min: 1, max: 10 });
    const entries: [unknown, unknown][] = Array.from({ length: count }, () => [
      generate(schema.def.keyType as z.ZodTypeAny),
      generate(schema.def.valueType as z.ZodTypeAny),
    ]);
    return new Map(entries);
  }
  if (schema instanceof z.ZodSet) {
    const count = faker.number.int({ min: 1, max: 10 });
    const values = Array.from({ length: count }, () =>
      generate(schema.def.valueType as z.ZodTypeAny),
    );
    return new Set(values);
  }
  if (schema instanceof z.ZodTuple) {
    return (schema.def.items as z.ZodTypeAny[]).map((s) => generate(s));
  }
  if (schema instanceof z.ZodIntersection) {
    const left = generate(schema.def.left as z.ZodTypeAny);
    const right = generate(schema.def.right as z.ZodTypeAny);
    if (
      typeof left === "object" &&
      left !== null &&
      typeof right === "object" &&
      right !== null
    ) {
      return {
        ...(left as Record<string, unknown>),
        ...(right as Record<string, unknown>),
      };
    }
    return left;
  }
  if (schema instanceof z.ZodPipe) {
    return generateFromPipe(schema);
  }
  if ((schema as any).constructor?.name === "ZodTransform") {
    const inner = (schema as any).def?.in;
    return inner ? generate(inner as z.ZodTypeAny, fieldName) : null;
  }
  if (schema instanceof z.ZodCustom) {
    return generateFromCustom(schema);
  }
  if (schema instanceof z.ZodVoid) {
    return undefined;
  }
  if (schema instanceof z.ZodLazy) {
    const getter = (schema as any).def?.getter;
    if (typeof getter === "function") {
      const inner = getter() as z.ZodTypeAny;
      if (lazySeenSchemas.has(inner)) {
        return null;
      }
      lazySeenSchemas.add(inner);
      const result = generate(inner, fieldName);
      lazySeenSchemas.delete(inner);
      return result;
    }
    return null;
  }
  if (schema instanceof z.ZodAny || schema instanceof z.ZodUnknown) {
    return null;
  }
  if (schema instanceof z.ZodNever) {
    return null;
  }
  // Fallback for string-format subtypes (ZodEmail, ZodURL, etc.)
  if ((schema as any).def?.type === "string") {
    return generateStringForField(fieldName);
  }
  console.warn(
    `[mock] Unhandled Zod schema type${fieldName ? ` for field "${fieldName}"` : ""}: ${schema.constructor.name}`,
    schema,
  );
  return null;
}

function generateFromPipe(schema: z.ZodPipe): unknown {
  // objectId() registers .meta({ annotation: "ObjectId" })
  const meta = (schema as any).meta?.();
  if (meta?.annotation === "ObjectId") {
    return faker.database.mongodbObjectId();
  }

  const innerSchema = schema.def.in;

  // filterToValidArray pattern: z.array(z.any()).transform(...)
  // Returns empty array — correct default (no items) avoids null-access crashes.
  if (
    innerSchema instanceof z.ZodArray &&
    innerSchema.element instanceof z.ZodAny
  ) {
    return [];
  }

  // Detect Temporal/Big custom types by inspecting the input schema's validator fn
  if (innerSchema instanceof z.ZodCustom) {
    const fnStr: string = (innerSchema as any).def?.fn?.toString() ?? "";
    if (fnStr.includes("PlainDate")) {
      return Temporal.Now.plainDateISO();
    }
    if (fnStr.includes("PlainTime")) {
      return Temporal.PlainTime.from("12:00:00");
    }
    if (fnStr.includes("Big")) {
      return "1.00";
    }
    // May be a z.preprocess() — def.in is the preprocessor, def.out is the real schema.
    // Exclude ZodCustom and ZodPipe from this branch to avoid matching .transform() chains.
    const outSchema = (schema as any).def?.out;
    if (
      outSchema &&
      !(outSchema instanceof z.ZodCustom) &&
      !(outSchema instanceof z.ZodPipe)
    ) {
      return generate(outSchema as z.ZodTypeAny);
    }
    // Unknown custom validator in a pipe — typically a string-format ID (e.g. prefixed IDs).
    // Return a non-null string rather than falling through to null.
    return faker.string.nanoid();
  }

  const generated = generate(innerSchema as z.ZodTypeAny);
  try {
    return schema.parse(generated);
  } catch {
    return generated;
  }
}

function generateFromCustom(schema: z.ZodCustom): unknown {
  const fnStr: string = (schema as any).def?.fn?.toString() ?? "";
  if (fnStr.includes("Temporal.Instant")) {
    return Temporal.Now.instant();
  }
  if (fnStr.includes("PlainDate")) {
    return Temporal.Now.plainDateISO();
  }
  if (fnStr.includes("PlainTime")) {
    return Temporal.PlainTime.from("12:00:00");
  }
  return null;
}
